import { useRef, useState } from 'react'
import { Check, Flag, MoreHorizontal } from 'lucide-react'
import type { NewsItem } from '../../types/news'
import { communityApi, communityError, type ClassificationReportReason } from '../../community/api'
import { useCommunity } from '../../community/CommunityProvider'
import { trackReaderEvent } from '../../services/analytics'
import { readerExperienceFlags } from '../../config/readerExperience'

const aiReasons: Array<{ value: ClassificationReportReason; label: string }> = [
  { value: 'not-about-ai', label: 'Not about AI' },
  { value: 'wrong-topic', label: 'Wrong topic' },
  { value: 'wrong-company', label: 'Wrong company' },
  { value: 'duplicate-story', label: 'Duplicate story' },
  { value: 'misleading-headline', label: 'Misleading headline' },
  { value: 'other', label: 'Other' },
]

const robotReasons: Array<{ value: ClassificationReportReason; label: string }> = [
  { value: 'not-about-robotics', label: 'Not about physical robotics' },
  { value: 'wrong-topic', label: 'Wrong robot desk' },
  { value: 'wrong-company', label: 'Wrong company' },
  { value: 'duplicate-story', label: 'Duplicate story' },
  { value: 'misleading-headline', label: 'Misleading headline' },
  { value: 'other', label: 'Other' },
]

const cybersecurityReasons: Array<{ value: ClassificationReportReason; label: string }> = [
  { value: 'not-about-cybersecurity', label: 'Not about cybersecurity' },
  { value: 'wrong-topic', label: 'Wrong cybersecurity desk' },
  { value: 'wrong-company', label: 'Wrong company' },
  { value: 'duplicate-story', label: 'Duplicate story' },
  { value: 'misleading-headline', label: 'Misleading headline' },
  { value: 'other', label: 'Other' },
]

export function StoryClassificationMenu({ item }: { item: NewsItem }) {
  const { requireAuth } = useCommunity()
  const details = useRef<HTMLDetailsElement>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  if (!readerExperienceFlags.wrongCategoryReporting) return null
  const reasons = item.edition === 'robots' ? robotReasons : item.edition === 'cybersecurity' ? cybersecurityReasons : aiReasons

  const report = (reason: ClassificationReportReason) => requireAuth('report', async () => {
    if (busy) return
    setBusy(true); setMessage('')
    try {
      await communityApi.reportStoryClassification(item.id, reason)
      setMessage('Sent for editorial review. The story is not removed automatically.')
      trackReaderEvent('wrong_category_report', { story_id: item.id, reason })
      if (details.current) details.current.open = false
    } catch (error) { setMessage(communityError(error)) } finally { setBusy(false) }
  })

  return <div className="story-classification-control">
    <details ref={details} className="story-classification-menu">
      <summary aria-label={`More options for ${item.title}`}><MoreHorizontal aria-hidden="true"/><span>More</span></summary>
      <div role="group" aria-label="Report wrong category"><strong><Flag aria-hidden="true"/>Report wrong category</strong>{reasons.map((reason) => <button type="button" disabled={busy} onClick={() => report(reason.value)} key={reason.value}>{reason.label}</button>)}</div>
    </details>
    {message && <p className="story-classification-status" role="status" aria-live="polite"><Check aria-hidden="true"/>{message}</p>}
  </div>
}
